// ---------------------------------------------------------------------------
// YouAreYou (uru) — Auth Context Types & Context Object
//
// This file contains ONLY the context definition and type exports.
// No React components live here — this satisfies eslint-plugin-react-refresh
// which requires that files exporting components don't also export
// non-component bindings (and vice versa).
//
// - AuthProvider component lives in @/context/AuthContext.tsx
// - useAuth hook lives in @/hooks/useAuth.ts
// ---------------------------------------------------------------------------

import { createContext } from "react";
import type { UserProfile } from "@/types/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AuthContextValue {
  /** Current authentication status. "loading" during initial session check. */
  status: AuthStatus;

  /** Authenticated user profile, or null if not logged in. */
  user: UserProfile | null;

  /** Whether the user just completed their first login and should register a passkey. */
  shouldPromptPasskey: boolean;
  setShouldPromptPasskey: (value: boolean) => void;

  // ---- Login Methods ----

  /** Exchange a magic link token (ml:...) for a session. */
  loginWithMagicLink: (token: string) => Promise<void>;

  /** Start and complete a passkey login flow for the given phone number. */
  loginWithPasskey: (phone: string) => Promise<void>;

  /** Send an SMS OTP to the given phone number. */
  sendSmsOtp: (phone: string) => Promise<void>;

  /** Verify the SMS OTP code and establish a session. */
  verifySmsOtp: (phone: string, code: string) => Promise<void>;

  // ---- Session Management ----

  /** Log out and clear all tokens. */
  logout: () => void;

  /** Refresh the user profile from the API. */
  refreshProfile: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const AuthContext = createContext<AuthContextValue | null>(null);
